import { format, isSameDay,differenceInYears, differenceInMonths } from 'date-fns';
import { ko } from 'date-fns/locale';

export function formatDate(dateString: string) {
  if (!dateString || dateString === null || dateString === undefined) {
    return '-';
  }

  const date = new Date(dateString);

  // Invalid Date 체크
  if (isNaN(date.getTime())) {
    return '-';
  }

  const today = new Date();

  // 당일이면 시간만 표시 (HH:mm)
  if (isSameDay(date, today)) {
    return format(date, 'HH:mm', { locale: ko });
  }

  // 당일이 아니면 yy.MM.dd 형식으로 표시
  return format(date, 'yy.MM.dd', { locale: ko });
}


export function formatDateTime(dateString: string) {
  if (dateString == null || dateString == undefined) return;

  const date = new Date(dateString);
  return format(date, 'yyyy.MM.dd. HH:mm', { locale: ko });
}

export function formatDateTimeWithSlash(dateString: string) {
  if (!dateString || dateString === null || dateString === undefined) {
    return '-';
  }
  
  const date = new Date(dateString);
  
  // Invalid Date 체크
  if (isNaN(date.getTime())) {
    return '-';
  }

  return format(date, 'yyyy/MM/dd/HH:mm', { locale: ko });
}

export function convertTo24HourTime(date: string, time: string): string {
  // HTML input에서 받은 date (YYYY-MM-DD)와 time (HH:MM) 포맷을 처리
  if (!date || !time) {
    throw new Error('Date and time are required');
  }

  // time이 이미 24시간 형식인지 확인
  if (time.includes(' ')) {
    // 12시간 형식 처리 (예: "3:00 오후")
    let [hourMinute, period] = time.split(" ");
    let [hours, minutes] = hourMinute.split(":");

    if (period === "오후" && hours !== "12") {
      hours = String(parseInt(hours, 10) + 12);
    } else if (period === "오전" && hours === "12") {
      hours = "00";
    }

    if (hours.length === 1) {
      hours = "0" + hours;
    }

    if (minutes.length === 1) {
      minutes = "0" + minutes;
    }

    const formattedTime = `${hours}:${minutes}:00`;
    const isoDateTime = `${date}T${formattedTime}`;
    return new Date(isoDateTime).toISOString();
  } else {
    // 24시간 형식 처리 (예: "15:00")
    const [hours, minutes] = time.split(":");
    const formattedTime = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
    const isoDateTime = `${date}T${formattedTime}`;
    return new Date(isoDateTime).toISOString();
  }
}

export function formatDateRange(startDate: string, endDate: string): string {
  if (!startDate || !endDate) {
    return '-';
  }
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return '-';
  }

  const startFormatted = format(start, 'MM.dd', { locale: ko });
  const endFormatted = format(end, 'MM.dd', { locale: ko });
  
  return `${startFormatted} ~ ${endFormatted}`;
}

export function calculateDuration(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);

  // 년도 차이 계산
  const years = differenceInYears(end, start);

  // 개월 차이 계산
  const months = differenceInMonths(end, start) % 12;

  return `${years}년 ${months}개월`;
}

export function formatMonthDay(dateString: string): string {
  if (!dateString || dateString === null || dateString === undefined) {
    return '-';
  }

  const date = new Date(dateString);

  // Invalid Date 체크
  if (isNaN(date.getTime())) {
    return '-';
  }

  // MM.dd 형식으로 표시
  return format(date, 'MM.dd', { locale: ko });
}
/**
 * 댓글·답글 날짜(짧게) — 속닥속닥·전문가 라운지 댓글 공용.
 * 오늘이면 "방금·N분 전·N시간 전", 올해면 "MM.dd HH:mm", 그 전 해면 "yy.MM.dd".
 */
export function formatCommentTime(dateString: string, now: Date = new Date()): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  if (isSameDay(date, now)) {
    const diffMin = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 60_000));
    if (diffMin < 1) return '방금';
    if (diffMin < 60) return `${diffMin}분 전`;
    return `${Math.floor(diffMin / 60)}시간 전`;
  }
  if (date.getFullYear() === now.getFullYear()) {
    return format(date, 'MM.dd HH:mm', { locale: ko });
  }
  return format(date, 'yy.MM.dd', { locale: ko });
}

/** 날짜만 yy.MM.dd — 오늘이어도 시간이 아니라 날짜로 보여야 하는 곳(지원 내역의 지원일·합격일 등) */
export function formatDateOnly(dateString?: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  return format(date, 'yy.MM.dd', { locale: ko });
}

/** 지원 결과 날짜 라벨 — 합격·불합격이고 처리 시각이 있을 때만 "합격일"/"불합격일", 아니면 null(줄·칸 숨김) */
export function applyResultDateLabel(status?: string | null, resultAt?: string | null): string | null {
  if (!resultAt) return null;
  const upper = status?.toUpperCase();
  if (upper === 'PASS') return '합격일';
  if (upper === 'DROP') return '불합격일';
  return null;
}
